<template>
  <div class="p-6 dark:text-gray-50">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Edit User</h1>
    </div>

    <form @submit.prevent="saveUser">
      <div class="p-6 space-y-6 glass-card">
        <div class="md:grid md:grid-cols-3 md:gap-6">
          <div class="md:col-span-1">
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-50">User Information</h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-50">
              Edit the user's basic information and role.
            </p>
          </div>
          <div class="mt-5 md:mt-0 md:col-span-2">
            <div class="grid grid-cols-6 gap-6">
              <div class="col-span-6 sm:col-span-4">
                <label
                  for="username"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-50"
                  >Email (Username)</label
                >
                <input
                  type="email"
                  id="username"
                  v-model="user.username"
                  class="pl-2 h-10 mt-1 block w-full rounded-md dark:bg-gray-600 dark:text-gray-50 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:ring-gray-400"
                />
              </div>

              <div class="col-span-6 sm:col-span-3">
                <label for="org" class="block text-sm font-medium text-gray-700 dark:text-gray-50"
                  >Organisation</label
                >
                <GlassSelect
                  id="org"
                  v-model="user.org"
                  :options="Object.values(organizations)"
                  placeholder="Select an organisation"
                />
              </div>

              <!-- Assigned Organizations -->
              <div v-if="selectedOrgIsServiceProvider" class="col-span-6">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-50 mb-2">
                  Assigned Client Organizations
                </label>
                <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Select which organizations this user can access
                </p>

                <div class="border border-gray-300 dark:border-gray-600 rounded-md max-h-60 overflow-y-auto p-3 space-y-2 bg-gray-50 dark:bg-gray-700">
                  <div
                    v-for="org in serviceProviderOrgs"
                    :key="org.id"
                    class="flex items-center"
                  >
                    <input
                      :id="`org-${org.id}`"
                      type="checkbox"
                      :value="org.id"
                      v-model="user.assigned_orgs"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      :for="`org-${org.id}`"
                      class="ml-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      {{ org.name }}
                    </label>
                  </div>

                  <div v-if="serviceProviderOrgs.length === 0" class="text-sm text-gray-500 dark:text-gray-400 italic">
                    No client organizations found for this service provider
                  </div>
                </div>
              </div>

              <div class="col-span-6">
                <div class="space-y-4">
                  <div class="flex items-start">
                    <div class="flex items-center h-5">
                      <input
                        id="is_active"
                        type="checkbox"
                        v-model="user.is_active"
                        class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                    <div class="ml-3 text-sm">
                      <label for="is_active" class="font-medium text-gray-700 dark:text-gray-50"
                        >Active</label
                      >
                      <p class="text-gray-500">
                        Allow this user to log in and access the system.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label class="block font-medium text-gray-700 dark:text-gray-50">Role</label>
                    <p class="text-sm text-gray-500 dark:text-gray-50 mb-2">
                      The user's access level.
                    </p>
                    <div class="flex flex-wrap gap-x-6 gap-y-2">
                      <label
                        v-for="opt in roleOptions"
                        :key="opt.value"
                        class="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="role"
                          :value="opt.value"
                          v-model="user.role"
                          class="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span class="text-sm text-gray-700 dark:text-gray-50">{{ opt.label }}</span>
                      </label>
                    </div>
                  </div>

                  <div class="flex items-start">
                    <div class="flex items-center h-5">
                      <input
                        id="is_subscribed_to_mivulite"
                        type="checkbox"
                        v-model="user.is_subscribed_to_mivulite"
                        class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                    <div class="ml-3 text-sm">
                      <label
                        for="is_subscribed_to_mivulite"
                        class="font-medium text-gray-700 dark:text-gray-50"
                        >Subscribed to Mivulite</label
                      >
                      <p class="text-gray-500 dark:text-gray-50">
                        Subscribes a user to mivulite
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="glass-card px-6 py-4 rounded-b-lg flex items-center justify-end space-x-3"
      >
        <router-link
          :to="{ name: 'UserDetails', params: { id: user.id } }"
          class="btn text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </router-link>
        <button
          type="submit"
          :disabled="loading"
          class="btn shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {{ loading ? "Saving..." : "Save Changes" }}
        </button>
      </div>
    </form>

    <div
      v-if="error"
      class="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md"
    >
      {{ error }}
    </div>
  </div>
</template>

<script>
import { getUserById, updateUser } from "@/services/userService";
import GlassSelect from "@/components/GlassSelect.vue";
import { getAllOrgs } from "@/services/orgService";

export default {
  name: "EditUser",
  components: {
    GlassSelect,
  },
  data() {
    return {
      originalAssignedOrgs: [],
      originalOrg: null,
      user: this.getEmptyUserObject(),
      loading: false,
      error: null,
      organizations: {},
    };
  },
  async created() {
    await this.fetchOrganizations();
    const userId = this.$route.params.id;
    await this.fetchUser(userId);
  },
  computed: {
    roleOptions() {
      return [
        { value: "viewer", label: "Viewer" },
        { value: "editor", label: "Editor" },
        { value: "admin", label: "Admin" },
      ];
    },
    selectedOrgIsServiceProvider() {
      if (!this.user.org) return false;
      const org = Object.values(this.organizations).find(
        (o) => o.id === parseInt(this.user.org)
      );
      if (!org) return false;
      if (org.name.toLowerCase() === "splitpoint") return false;
      return org?.is_service_provider || false;
    },

    serviceProviderOrgs() {
      if (!this.selectedOrgIsServiceProvider) return [];
      return Object.values(this.organizations).filter(
        (org) => org.service_provider === parseInt(this.user.org)
      );
    },
  },
  methods: {
    getEmptyUserObject() {
      return {
        id: null,
        username: "",
        email: "",
        first_name: "",
        last_name: "",
        is_active: true,
        role: "viewer",
        org: "",
        assigned_orgs: [],
        is_subscribed_to_mivulite: false,
      };
    },
    async fetchUser(userId) {
      try {
        const userData = await getUserById(userId);
        this.user = userData;
        this.originalAssignedOrgs = [...(userData.assigned_orgs || [])];
        this.originalOrg = userData.org;
      } catch (error) {
        console.error("Error fetching user:", error);
        this.error = "Failed to load user";
      }
    },
    async fetchOrganizations() {
      try {
        const orgs = await getAllOrgs();
        this.organizations = orgs.reduce((acc, org) => {
          acc[org.id] = org;
          return acc;
        }, {});
      } catch (error) {
        console.error("Error fetching organizations:", error);
        this.error = "An error occurred while fetching organizations.";
      }
    },
    async saveUser() {
      try {
        this.loading = true;
        this.error = null;

        const userData = {
          ...this.user,
          assigned_orgs: (this.user.assigned_orgs || []).map((id) =>
            parseInt(id, 10)
          ),
        };

        await updateUser(this.user.id, userData);
        this.$router.push({
          name: "UserDetails",
          params: { id: this.user.id },
        });
      } catch (error) {
        console.error("Error saving user:", error);
        const data = error.response?.data;
        if (data && typeof data === "object") {
          const key = Object.keys(data)[0];
          const msg = Array.isArray(data[key]) ? data[key][0] : data[key];
          this.error = key ? `${key}: ${msg}` : "Failed to save user changes";
        } else {
          this.error = "Failed to save user changes";
        }
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    "user.org"(newValue, oldValue) {
      if (newValue === this.originalOrg) {
        this.user.assigned_orgs = [...this.originalAssignedOrgs];
      } else if (oldValue && newValue !== oldValue) {
        this.user.assigned_orgs = [];
      }
    },
  },
};
</script>
